import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { accessToken } from './auth/cognito.ts';
import { RequireMfa } from './components/require-mfa.tsx';
import { Shell } from './components/shell.tsx';
import { ClientsScreen } from './routes/clients.tsx';
import { CrewScreen } from './routes/crew.tsx';
import { MyShiftsScreen } from './routes/my-shifts.tsx';
import { ROSTER_DAYS, RosterScreen, type RosterDays, type RosterSearch } from './routes/roster.tsx';
import { HomeScreen } from './routes/home.tsx';
import { InventoryScreen } from './routes/inventory.tsx';
import { CreditNoteScreen } from './routes/credit-note.tsx';
import { CreditReasonsScreen, CreditsScreen } from './routes/credits.tsx';
import { InvoiceScreen } from './routes/invoice.tsx';
import { InvoicesScreen } from './routes/invoices.tsx';
import { StatementScreen, StatementsScreen } from './routes/statements.tsx';
import { PricingScreen } from './routes/pricing.tsx';
import { JobScreen } from './routes/job.tsx';
import { JobsScreen } from './routes/jobs.tsx';
import { QuoteScreen } from './routes/quote.tsx';
import { QuotesScreen } from './routes/quotes.tsx';
import { ProductScreen } from './routes/product.tsx';
import { SignInScreen } from './routes/sign-in.tsx';
import { VenueScreen, type VenueTab } from './routes/venue.tsx';
import { VenuesScreen } from './routes/venues.tsx';
import { DocumentsScreen } from './routes/documents.tsx';
import { InsuranceScreen } from './routes/insurance.tsx';
import { SafetyDocumentScreen, SafetyScreen } from './routes/safety.tsx';

// Routing is structural, not decorative: the authenticated area is a layout route with the guard on
// it, so a screen added underneath is authenticated by construction rather than by remembering to
// add a check. The same reason the API's guard is global and @Public() is the opt-out.

export interface RouterContext {
  /** Resolves to the access token, or null. Read per navigation so a signed-out tab redirects. */
  readonly token: () => Promise<string | null>;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInScreen,
  async beforeLoad({ context }) {
    // Somebody already signed in has no business on this screen; the back button is the usual way
    // to arrive here.
    if (await context.token()) throw redirect({ to: '/' });
  },
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  async beforeLoad({ context }) {
    if (!(await context.token())) throw redirect({ to: '/sign-in' });
  },
  component: () => (
    <RequireMfa>
      <Shell>
        <Outlet />
      </Shell>
    </RequireMfa>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: HomeScreen,
});

const clientsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/clients',
  component: ClientsScreen,
});

const inventoryRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/inventory',
  component: InventoryScreen,
});

const productRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/inventory/$productId',
  component: ProductScreen,
});

const pricingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/pricing',
  component: PricingScreen,
});

const quotesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/quotes',
  validateSearch: (search: Record<string, unknown>): { venue?: string } =>
    typeof search['venue'] === 'string' && UUID.test(search['venue'])
      ? { venue: search['venue'] }
      : {},
  component: QuotesScreen,
});

const quoteRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/quotes/$quoteId',
  component: QuoteScreen,
});

const jobsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/jobs',
  component: JobsScreen,
});

const jobRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/jobs/$jobId',
  component: JobScreen,
});

const invoicesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/invoices',
  component: InvoicesScreen,
});

const invoiceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/invoices/$invoiceId',
  component: InvoiceScreen,
});

// CB-REQ FIN-02 at v1.993: credits and their reasons, under Invoices.
const creditNotesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/credit-notes',
  component: CreditsScreen,
});

const creditNoteRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/credit-notes/$creditNoteId',
  component: CreditNoteScreen,
});

const creditReasonsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/credit-note-reasons',
  component: CreditReasonsScreen,
});

// CB-REQ VEN-11 at v1.990: the venues' monthly statements, under Invoices.
const statementsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/statements',
  validateSearch: (search: Record<string, unknown>): { month?: string } =>
    typeof search['month'] === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(search['month'])
      ? { month: search['month'] }
      : {},
  component: StatementsScreen,
});

const statementRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/statements/$venueId/$month',
  validateSearch: (search: Record<string, unknown>): { kind?: 'resale' | 'direct_bill' } =>
    search['kind'] === 'resale' || search['kind'] === 'direct_bill' ? { kind: search['kind'] } : {},
  component: StatementScreen,
});

const venuesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/venues',
  component: VenuesScreen,
});

const venueRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/venues/$venueId',
  // VEN-01 at v1.9104: the tab is in the URL, so a link can open Rules or Money directly.
  validateSearch: (search: Record<string, unknown>): { tab?: VenueTab } =>
    typeof search['tab'] === 'string' &&
    ['rooms', 'access', 'equipment', 'rules', 'money'].includes(search['tab'])
      ? { tab: search['tab'] as VenueTab }
      : {},
  component: VenueScreen,
});

const documentsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/documents',
  component: DocumentsScreen,
});

const insuranceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/insurance',
  component: InsuranceScreen,
});

const safetyRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/safety',
  component: SafetyScreen,
});

const safetyDocumentRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/safety/$documentId',
  component: SafetyDocumentScreen,
});

const crewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/crew',
  component: CrewScreen,
});

// CB-REQ OPS-03 at v1.998: the range and filters are in the URL, so "Open in roster" is a link.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const rosterRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/roster',
  validateSearch: (search: Record<string, unknown>): RosterSearch => {
    const out: RosterSearch = {};
    const { from, days, job, venue } = search;
    if (typeof from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(from)) out.from = from;
    const n = Number(days);
    if ((ROSTER_DAYS as readonly number[]).includes(n)) out.days = n as RosterDays;
    if (typeof job === 'string' && UUID.test(job)) out.job = job;
    if (typeof venue === 'string' && UUID.test(venue)) out.venue = venue;
    return out;
  },
  component: RosterScreen,
});

const myShiftsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/my-shifts',
  component: MyShiftsScreen,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  authenticatedRoute.addChildren([
    homeRoute,
    clientsRoute,
    inventoryRoute,
    productRoute,
    pricingRoute,
    quotesRoute,
    quoteRoute,
    jobsRoute,
    jobRoute,
    invoicesRoute,
    invoiceRoute,
    creditNotesRoute,
    creditNoteRoute,
    creditReasonsRoute,
    statementsRoute,
    statementRoute,
    venuesRoute,
    venueRoute,
    crewRoute,
    rosterRoute,
    myShiftsRoute,
    documentsRoute,
    insuranceRoute,
    safetyRoute,
    safetyDocumentRoute,
  ]),
]);

export function buildRouter(context: RouterContext) {
  return createRouter({ routeTree, context, defaultPreload: 'intent' });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}

export const router = buildRouter({ token: accessToken });
